import { NAVIGATION_SETTINGS, SECTION } from "constants/SettingsConstants";
import { get as lockrGet, set as lockrSet } from "lockr";
import { isBoolean } from "lodash";
import type { UserType, ValidationDataType } from "types/DataTypes.ts";

import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";

type InitialState = {
  sideNavigationActive: boolean;
  currentSection: string;

  /* When validationErrors is set (aka non-zero), the right side panel
  error bubble appears and if you click the red button the drawer opens with more info */
  validationErrors: ValidationDataType[];
  // validationWarnings is used to display the number of warnings in the right sidebar
  validationWarnings: ValidationDataType[];

  /* Toggles when you hit the "+" in the right panel to add a new item: claim, rre, rretin, client, etc. */
  showAddModal: boolean;
  /* Adding a new variable to toggle specific a modal when multiple modals are present */
  showAddClaimantModal: boolean;
  showAddClientModal: boolean;
  showTaskModal: boolean;
  showSendToOpsModal: boolean;
  showUploadClaimsOnFileModal: boolean;
  showCreateReReviewModal: boolean;
  showErrorModal: boolean;
  showUploadTemplateModal: boolean;
  downloadTemplate: boolean;
  loggedInUser: UserType | null;
  accessToken: string | null;
  isLoading: boolean;
  isFileLoading: boolean;
  searchFilterActive: boolean;
  showSubHeader: boolean;
  lienDirectorySearchTerm: string | null;
  focusedField: string | null;
  // demo feature: override the medical review status to yellow
  medicalReviewOverride: boolean;
  // demo feature: override the legal review status to yellow
  legalReviewOverride: boolean;
  // demo feature: override the CMS status to green
  cmsStatusOverride: boolean;
  // track whether we've initialized the completed tasks filter
  completedTasksFilterInitialized: boolean;
};

const getInitialNavigationState = () => {
  const savedState = lockrGet(NAVIGATION_SETTINGS);
  if (typeof window !== "undefined" && window.innerWidth <= 1024) {
    return true;
  }
  return isBoolean(savedState) ? savedState : false;
};

const getInitialState = (): InitialState => ({
  sideNavigationActive: getInitialNavigationState(),
  currentSection: SECTION.SECTION_111_DASHBOARD,
  validationErrors: [],
  validationWarnings: [],
  showAddModal: false,
  showUploadTemplateModal: false,
  downloadTemplate: false,
  showAddClaimantModal: false,
  showTaskModal: false,
  showSendToOpsModal: false,
  showUploadClaimsOnFileModal: false,
  showCreateReReviewModal: false,
  showAddClientModal: false,
  showErrorModal: false,
  loggedInUser: null,
  accessToken: null,
  searchFilterActive: false,
  isLoading: false,
  isFileLoading: false,
  showSubHeader: false,
  lienDirectorySearchTerm: null,
  focusedField: null,
  medicalReviewOverride: false,
  legalReviewOverride: false,
  cmsStatusOverride: false,
  completedTasksFilterInitialized: false,
});

const kaboodleSlice = createSlice({
  name: "main",
  initialState: getInitialState(),
  reducers: {
    setFocusedField: (state, action: PayloadAction<string | null>) => {
      state.focusedField = action.payload;
    },
    toggleAccessPanels(state) {
      lockrSet(NAVIGATION_SETTINGS, !state.sideNavigationActive);
      state.sideNavigationActive = !state.sideNavigationActive;
    },
    setCurrentSection(state, action: PayloadAction<string>) {
      state.currentSection = action.payload;
    },
    setLienDirectorySearchTerm(state, action: PayloadAction<string | null>) {
      state.lienDirectorySearchTerm = action.payload;
    },
    setValidationState: (
      state,
      action: PayloadAction<{
        errors: ValidationDataType[];
        warnings: ValidationDataType[];
      }>,
    ) => {
      state.validationErrors = action.payload.errors;
      state.validationWarnings = action.payload.warnings;
    },
    setValidationErrors: (
      state,
      action: PayloadAction<ValidationDataType[]>,
    ) => {
      state.validationErrors = action.payload;
      state.validationWarnings = [];
    },
    setValidationWarnings: (
      state,
      action: PayloadAction<ValidationDataType[]>,
    ) => {
      state.validationWarnings = action.payload;
      state.validationErrors = [];
    },
    setShowAddModal: (state, action: PayloadAction<boolean>) => {
      state.showAddModal = action.payload;
    },
    setShowTaskModal: (state, action: PayloadAction<boolean>) => {
      state.showTaskModal = action.payload;
    },
    setShowSendToOpsModal: (state, action: PayloadAction<boolean>) => {
      state.showSendToOpsModal = action.payload;
    },
    setShowUploadClaimsOnFileModal: (state, action: PayloadAction<boolean>) => {
      state.showUploadClaimsOnFileModal = action.payload;
    },
    setShowCreateReReviewModal: (state, action: PayloadAction<boolean>) => {
      state.showCreateReReviewModal = action.payload;
    },
    setShowUploadTemplateModal: (state, action: PayloadAction<boolean>) => {
      state.showUploadTemplateModal = action.payload;
    },
    setDownloadTemplate: (state, action: PayloadAction<boolean>) => {
      state.downloadTemplate = action.payload;
    },
    setShowAddClaimantModal: (state, action: PayloadAction<boolean>) => {
      state.showAddClaimantModal = action.payload;
    },
    setShowAddClientModal: (state, action: PayloadAction<boolean>) => {
      state.showAddClientModal = action.payload;
    },
    setShowErrorModal: (state, action: PayloadAction<boolean>) => {
      state.showErrorModal = action.payload;
    },
    setLoggedInUser: (state, action: PayloadAction<UserType>) => {
      state.loggedInUser = action.payload;
    },
    setAccessToken: (state, action: PayloadAction<string | null>) => {
      state.accessToken = action.payload;
    },
    toggleSearchFilter(state) {
      state.searchFilterActive = !state.searchFilterActive;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setIsFileLoading: (state, action: PayloadAction<boolean>) => {
      state.isFileLoading = action.payload;
    },
    setShowSubHeader: (state, action: PayloadAction<boolean>) => {
      state.showSubHeader = action.payload;
    },
    clearValidationState: (state) => {
      state.validationErrors = [];
      state.validationWarnings = [];
    },
    // toggle medical review override for demo purposes
    toggleMedicalReviewOverride: (state) => {
      state.medicalReviewOverride = !state.medicalReviewOverride;
    },
    // toggle legal review override for demo purposes
    toggleLegalReviewOverride: (state) => {
      state.legalReviewOverride = !state.legalReviewOverride;
    },
    // toggle CMS status override for demo purposes
    toggleCmsStatusOverride: (state) => {
      state.cmsStatusOverride = !state.cmsStatusOverride;
    },
    // mark completed tasks filter as initialized
    setCompletedTasksFilterInitialized: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.completedTasksFilterInitialized = action.payload;
    },
  },
});

const { reducer } = kaboodleSlice;

export const {
  setLienDirectorySearchTerm,
  setLoggedInUser,
  setAccessToken,
  setValidationErrors,
  setValidationWarnings,
  clearValidationState,
  toggleAccessPanels,
  setCurrentSection,
  setShowAddModal,
  setShowAddClaimantModal,
  setShowAddClientModal,
  toggleSearchFilter,
  setIsLoading,
  setIsFileLoading,
  setShowSubHeader,
  setValidationState,
  setDownloadTemplate,
  setShowUploadTemplateModal,
  setShowTaskModal,
  setShowSendToOpsModal,
  setShowUploadClaimsOnFileModal,
  setShowErrorModal,
  setShowCreateReReviewModal,
  setFocusedField,
  toggleMedicalReviewOverride,
  toggleLegalReviewOverride,
  toggleCmsStatusOverride,
  setCompletedTasksFilterInitialized,
} = kaboodleSlice.actions;

export default reducer;
